/* ═══════════════════════════════════════════════════════════════
   PiARIS — Side Rails
   Educational "Did you know?" callout cards on left/right margins.
   Always expanded — shows the full fact. Color-coded by category.
   Speech-bubble tail points toward the main content.

   Sticky positioned, hidden on mobile (<1100px viewport).
   ADA compliant: role="complementary", semantic markup, screen-reader
   readable, keyboard focusable.
   © 2026 Pi Consultants · pi-consulting.us
   ═══════════════════════════════════════════════════════════════ */
import React from "react";
import { P } from "../constants";
import { ProductStoryRail } from "./ProductStoryRail";

var E = React.createElement;

/* ── Color themes per category ── */
var THEMES = {
  legal:     { bg: "#FFFBEB", border: "#F59E0B", accent: "#B45309", badge: "#FEF3C7", text: "#78350F" },
  technical: { bg: "#EFF6FF", border: "#3B82F6", accent: "#1D4ED8", badge: "#DBEAFE", text: "#1E3A5F" },
  impact:    { bg: "#F5F3FF", border: "#8B5CF6", accent: "#6D28D9", badge: "#EDE9FE", text: "#4C1D95" },
  practice:  { bg: "#ECFDF5", border: "#10B981", accent: "#047857", badge: "#D1FAE5", text: "#064E3B" },
  global:    { bg: "#FFF1F2", border: "#F43F5E", accent: "#BE123C", badge: "#FFE4E6", text: "#881337" }
};

/* ── Educational facts ── */
var A11Y_FACTS = [
  { icon: "\u2696\uFE0F", cat: "Section 508", theme: "legal",
    fact: "Section 508 requires all federal agencies to make electronic and information technology accessible. Non-compliance can result in complaints filed with the agency\u2019s CIO and legal action under the Rehabilitation Act." },
  { icon: "\uD83C\uDFDB\uFE0F", cat: "ADA Title II", theme: "legal",
    fact: "As of April 2024, state and local governments must make their websites accessible under ADA Title II. Compliance deadlines: April 2026 for large entities, April 2027 for smaller ones." },
  { icon: "\uD83D\uDC41\uFE0F", cat: "Perceivable", theme: "technical",
    fact: "All content must be presentable in ways users can perceive \u2014 text alternatives for images, captions for video, and sufficient color contrast (4.5:1 minimum for normal text)." },
  { icon: "\u2328\uFE0F", cat: "Operable", theme: "technical",
    fact: "Every interactive element must work via keyboard alone. If a mouse user can click it, a keyboard user must Tab to it and activate with Enter or Space. No keyboard traps allowed." },
  { icon: "\uD83D\uDCA1", cat: "Understandable", theme: "technical",
    fact: "Page language must be declared in HTML. Without <html lang=\"en\">, screen readers may mispronounce every word on the page \u2014 a silent but severe barrier for blind users." },
  { icon: "\uD83D\uDD27", cat: "Robust", theme: "technical",
    fact: "Content must work with current and future assistive technologies. Valid HTML and proper ARIA roles keep your site working with screen readers that haven\u2019t been built yet." },
  { icon: "\uD83C\uDF10", cat: "WCAG 2.2", theme: "technical",
    fact: "WCAG 2.2 added 9 new success criteria including Focus Appearance (visible focus rings), Dragging Movements (alternatives to drag-and-drop), and minimum 24\u00D724px touch targets." },
  { icon: "\uD83D\uDCC4", cat: "PDF/UA", theme: "technical",
    fact: "PDF/UA (ISO 14289) requires every PDF to have a tag tree, declared language, Unicode mapping, and alt text for all images. Untagged PDFs are completely invisible to screen readers." },
  { icon: "\u267F", cat: "A11Y", theme: "practice",
    fact: "\"a11y\" is a numeronym for \"accessibility\" \u2014 the 11 stands for the letters between \u2018a\u2019 and \u2018y\u2019. It\u2019s common shorthand in the accessibility community." },
  { icon: "\uD83C\uDFAF", cat: "Color Contrast", theme: "practice",
    fact: "Text under 18px needs a 4.5:1 contrast ratio. Large text (18px+ or 14px bold) needs 3:1. This single rule catches approximately 30% of all web accessibility issues found in audits." },
  { icon: "\uD83D\uDCF1", cat: "Mobile A11Y", theme: "practice",
    fact: "Over 70% of screen reader users browse on mobile. Touch targets under 44\u00D744px cause misclicks for users with motor impairments. WCAG 2.2 set the minimum at 24\u00D724px \u2014 still smaller than a fingertip." },
  { icon: "\uD83C\uDDEA\uD83C\uDDFA", cat: "European Access Act", theme: "global",
    fact: "The European Accessibility Act (EN 301 549) takes effect June 2025. It applies to private-sector products and services sold in the EU \u2014 not just government sites. Fines vary by member state." },
  { icon: "\u{1F9E0}", cat: "Cognitive A11Y", theme: "practice",
    fact: "Cognitive accessibility — clear language, consistent navigation, and error prevention — helps users with dyslexia, ADHD, and age-related memory changes." },
  { icon: "\uD83D\uDCCA", cat: "Global Impact", theme: "global",
    fact: "1 in 4 US adults has a disability \u2014 1.3 billion people worldwide. Inaccessible websites shut out roughly 16% of the world\u2019s population, and the trillions in spending power they hold." },
];

function pickFacts(n) {
  var pool = A11Y_FACTS.slice();
  var picked = [];
  for (var i = 0; i < n && pool.length > 0; i++) {
    var idx = Math.floor(Math.random() * pool.length);
    picked.push(pool.splice(idx, 1)[0]);
  }
  return picked;
}

/** CalloutCard \u2014 a single color-coded educational callout. Always expanded. */
function CalloutCard(p) {
  var fact = p.fact;
  var isLeft = p.side === "left";
  var t = THEMES[fact.theme] || THEMES.technical;

  return E("div", {
    role: "note",
    "aria-label": fact.cat + " \u2014 accessibility insight",
    tabIndex: 0,
    style: {
      position: "relative",
      background: t.bg,
      border: "1px solid " + t.border + "40",
      borderLeft: isLeft ? "4px solid " + t.border : "1px solid " + t.border + "40",
      borderRight: isLeft ? "1px solid " + t.border + "40" : "4px solid " + t.border,
      borderRadius: 14,
      padding: "14px 16px",
      boxShadow: "0 2px 12px " + t.border + "15",
      transition: "transform 0.15s ease, box-shadow 0.15s ease"
    },
    onMouseEnter: function (e) { e.currentTarget.style.transform = "scale(1.02)"; e.currentTarget.style.boxShadow = "0 4px 20px " + t.border + "25"; },
    onMouseLeave: function (e) { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.boxShadow = "0 2px 12px " + t.border + "15"; },
    onFocus: function (e) { e.currentTarget.style.boxShadow = "0 0 0 3px " + t.border + "40"; },
    onBlur: function (e) { e.currentTarget.style.boxShadow = "0 2px 12px " + t.border + "15"; }
  },
    /* Speech-bubble tail pointing toward the center content */
    E("div", { "aria-hidden": "true", style: {
      position: "absolute", top: 20,
      [isLeft ? "right" : "left"]: -8,
      width: 0, height: 0,
      borderTop: "8px solid transparent",
      borderBottom: "8px solid transparent",
      [isLeft ? "borderLeft" : "borderRight"]: "8px solid " + t.bg
    }}),

    /* Badge row: icon + category */
    E("div", { style: { display: "flex", alignItems: "center", gap: 8, marginBottom: 10 } },
      E("span", { style: {
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        width: 32, height: 32, borderRadius: 10,
        background: t.badge, fontSize: 16, flexShrink: 0
      }}, fact.icon),
      E("span", { style: {
        fontSize: 10, fontWeight: 800, letterSpacing: ".06em",
        textTransform: "uppercase", color: t.accent, lineHeight: 1.2
      }}, fact.cat)
    ),

    /* "Did you know?" + fact text */
    E("div", { style: {
      fontSize: 11.5, lineHeight: 1.6, color: t.text
    }},
      E("span", { style: { fontWeight: 800, color: t.accent } }, "Did you know? "),
      fact.fact
    ),

    /* Subtle source line */
    E("div", { style: {
      fontSize: 8, color: t.accent + "80", marginTop: 8,
      letterSpacing: ".04em", fontWeight: 600
    }}, "\uD83D\uDCA1 PiARIS A11Y Insights")
  );
}

/** SideRail \u2014 vertical column of always-expanded callout cards. */
function SideRail(p) {
  var isLeft = p.side === "left";
  var facts = React.useMemo(function () { return pickFacts(p.count || 3); }, []);

  return E("aside", {
    "aria-label": (isLeft ? "Left" : "Right") + " sidebar \u2014 accessibility education",
    className: "sideRail sideRail-" + p.side,
    style: {
      position: "sticky",
      top: 80,
      width: 220,
      flexShrink: 0,
      alignSelf: "flex-start",
      display: "flex",
      flexDirection: "column",
      gap: 16,
      padding: "8px 0"
    }
  },
    facts.map(function (fact, i) {
      return E(CalloutCard, { key: i, fact: fact, side: p.side });
    })
  );
}

/** PageWithSideRails \u2014 wrapper adding educational side rails. Hidden <1100px. */
function PageWithSideRails(p) {
  return E("div", {
    style: {
      display: "flex",
      gap: 20,
      alignItems: "flex-start",
      maxWidth: 1520,
      margin: "0 auto",
      padding: "0 12px"
    }
  },
    E(SideRail, { side: "left", count: 3 }),
    E("div", { style: { flex: 1, minWidth: 0 } }, p.children),
    E(ProductStoryRail, null)   /* right rail: "See it work" story (was a 2nd fact rail) */
  );
}

export { SideRail, PageWithSideRails };
